import React, { useEffect, useState } from 'react';
import { RestClient } from '../services/clients/RestClient';
import { AuthService } from '../services/users/AuthService';
import { UserService } from '../services/users/UserService';
import {
  ILoginData,
  ILoginRequest,
} from '../services/users/UserService.interface';

export const authService = new AuthService();
export const userService = new UserService(
  new RestClient({ baseURL: process.env.REACT_APP_API_BASE_URL }),
  authService,
);

type AuthState = {
  login?: (request: ILoginRequest) => Promise<void>;
  logout?: () => Promise<void>;
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  data: ILoginData | null;
};

type AuthContextProps = AuthState | undefined;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  data: null,
};

const AuthContext = React.createContext<AuthContextProps>(undefined);
AuthContext.displayName = 'AuthContext';

const AuthProvider: React.FC = props => {
  const { children } = props;

  const [loginState, setLoginState] = useState<AuthState>(initialAuthState);

  useEffect(() => {
    setLoginState({
      isAuthenticated: authService.isAuthenticated(),
      accessToken: authService.getToken('access-token'),
      refreshToken: authService.getToken('refresh-token'),
      data: null,
    });
  }, []);

  const login = React.useCallback(
    (request: ILoginRequest) =>
      userService.login(request).then(response =>
        setLoginState({
          isAuthenticated: authService.isAuthenticated(),
          accessToken: authService.getToken('access-token'),
          refreshToken: authService.getToken('refresh-token'),
          data: response.data,
        }),
      ),
    [setLoginState],
  );

  const logout = React.useCallback(
    () => userService.logout().then(() => setLoginState(initialAuthState)),
    [setLoginState],
  );

  const value = React.useMemo(
    () => ({
      login,
      logout,
    }),
    [login, logout],
  );

  const authContextValues = {
    isAuthenticated: loginState.isAuthenticated,
    accessToken: loginState.accessToken,
    refreshToken: loginState.refreshToken,
    data: loginState.data,
    ...value,
  } as AuthContextProps;

  return (
    <AuthContext.Provider value={authContextValues}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must use under AuthProvider');
  }
  return context;
};

export { AuthProvider, useAuth };
